// piano-frontend/src/components/PushNotificationManager.js
import React, { useState, useEffect } from 'react';
import { Box, Button, Typography, Alert, Paper, Switch, FormControlLabel, Snackbar } from '@mui/material';
import { Notifications, NotificationsOff } from '@mui/icons-material';
import api from '../api/axios';
import { useDialog } from '../contexts/DialogProvider';

// 플랫폼별 알림 권한 설정 안내 경로
const getPermissionGuide = () => {
  const ua = navigator.userAgent;
  if (/iPhone|iPad/.test(ua)) {
    return {
      platform: 'iOS',
      steps: '기기 설정 → 앱 이름 → 알림 → 알림 허용',
      detail: 'PWA를 홈 화면에 추가한 경우: iPhone 설정 앱 → 해당 앱 → 알림 → 알림 허용으로 변경하세요.'
    };
  }
  if (/Android/.test(ua)) {
    return {
      platform: 'Android',
      steps: '브라우저 주소창 자물쇠 아이콘 → 사이트 설정 → 알림 → 허용',
      detail: 'Chrome: 주소창 왼쪽 자물쇠(또는 ⓘ) 아이콘 → 사이트 설정 → 알림 → 허용으로 변경하세요.'
    };
  }
  return {
    platform: null,
    steps: '브라우저 주소창 자물쇠 아이콘 → 사이트 설정 → 알림 → 허용',
    detail: '브라우저 주소창 옆 자물쇠(또는 ⓘ) 아이콘을 눌러 알림 권한을 허용으로 변경하세요.'
  };
};

function PushNotificationManager({ user, onSubscribed, onStatusChange }) {
  const { toast } = useDialog();
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [vapidPublicKey, setVapidPublicKey] = useState('');
  const [showResubscribePrompt, setShowResubscribePrompt] = useState(false);
  const [checking, setChecking] = useState(true);
  const [permissionDenied, setPermissionDenied] = useState(false);

  useEffect(() => {
    console.log('[Push] 컴포넌트 마운트, 알림 권한:', Notification.permission);

    if (Notification.permission === 'denied') {
      setPermissionDenied(true);
    }

    if ('serviceWorker' in navigator && 'PushManager' in window) {
      setIsSupported(true);
      initializePushNotifications();
    } else {
      console.log('[Push] 브라우저가 푸시를 지원하지 않음');
      setChecking(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ✅ isSubscribed 상태 변경 시 부모 컴포넌트에 알림
  useEffect(() => {
    if (onStatusChange) {
      onStatusChange(isSubscribed);
    }
  }, [isSubscribed, onStatusChange]);

  useEffect(() => {
    const checkVapidChange = async () => {
      if (!isSubscribed) return;
      
      try {
        const registration = await navigator.serviceWorker.ready;
        const currentSub = await registration.pushManager.getSubscription();
        
        if (!currentSub) return;
        
        const res = await api.get('/config/vapid-public-key');
        const serverVapidKey = res.data.publicKey;
        
        const lastVapidKey = localStorage.getItem('last_vapid_key');
        
        if (lastVapidKey && lastVapidKey !== serverVapidKey) {
          console.log('⚠️ VAPID 키 변경 감지 - 자동 재구독 시작');
          
          await currentSub.unsubscribe();
          await subscribeToPush(true);
        }
        
        localStorage.setItem('last_vapid_key', serverVapidKey);
      } catch (error) {
        console.error('VAPID 변경 확인 실패:', error);
      }
    };
    
    checkVapidChange();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubscribed]);

  const initializePushNotifications = async () => {
    try {
      const res = await api.get('/config/vapid-public-key');
      if (res.data.configured) {
        setVapidPublicKey(res.data.publicKey);
      }

      await checkAndValidateSubscription();
    } catch (err) {
      console.error('[Push Init] 초기화 실패:', err);
    } finally {
      setChecking(false);
    }
  };

  const checkAndValidateSubscription = async () => {
    try {
      const registration = await navigator.serviceWorker.ready;
      const existingSubscription = await registration.pushManager.getSubscription();

      console.log('[Push] 기기 구독 상태:', existingSubscription ? '있음' : '없음');

      if (!existingSubscription) {
        console.log('[Push] 기기에 구독이 없음');
        setIsSubscribed(false);
        
        const res = await api.get('/push/status');
        console.log('[Push] 백엔드 구독 상태:', res.data);
        
        if (res.data.isSubscribed) {
          console.log('[Push] 백엔드에는 구독 존재 - 불일치 감지');
          if (Notification.permission === 'granted') {
            console.log('[Push] 자동 재등록 시도');
            await subscribeToPush(true);
          }
        }
        return;
      }

      console.log('[Push] 기기에 구독 있음 - 서버 검증 시작');

      const validateRes = await api.post('/push/validate', {
        subscription: existingSubscription.toJSON()
      });

      console.log('[Push] 서버 검증 결과:', validateRes.data);

      if (validateRes.data.needsResubscription) {
        console.log('[Push] 구독이 만료됨 - 재등록 필요');
        setIsSubscribed(false);
        setShowResubscribePrompt(true);
      } else {
        console.log('[Push] 구독 상태 정상');
        setIsSubscribed(true);
        if (onSubscribed) onSubscribed();
      }
    } catch (error) {
      console.error('[Push] 구독 확인 실패:', error);
      setIsSubscribed(false);
      
      if (!error.message?.includes('Network Error')) {
        setShowResubscribePrompt(true);
      }
    }
  };

  const subscribeToPush = async (silent = false) => {
    if (loading) return;

    try {
      setLoading(true);
      setError('');

      if (!vapidPublicKey) {
        setError('푸시 서비스가 구성되지 않았습니다.');
        return;
      }

      if (!silent) {
        // 이미 거부된 경우 requestPermission 호출 없이 안내만 표시
        if (Notification.permission === 'denied') {
          setPermissionDenied(true);
          return;
        }
        const permission = await Notification.requestPermission();
        if (permission === 'denied') {
          setPermissionDenied(true);
          return;
        }
        if (permission !== 'granted') {
          setError('알림 권한이 필요합니다. 다시 시도해 주세요.');
          return;
        }
      } else {
        if (Notification.permission !== 'granted') {
          console.log('[Push] Silent 모드지만 권한 없음 - 중단');
          return;
        }
      }

      const registration = await navigator.serviceWorker.ready;

      const existingSubscription = await registration.pushManager.getSubscription();
      if (existingSubscription) {
        await existingSubscription.unsubscribe();
        console.log('[Push] 기존 구독 삭제 완료');
      }

      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
      });

      await api.post('/push/subscribe', {
        subscription: subscription.toJSON()
      });

      setIsSubscribed(true);
      setShowResubscribePrompt(false);
      if (onSubscribed) onSubscribed();
      
      if (!silent) {
        toast('푸시 알림이 활성화되었습니다!');
      } else {
        console.log('[Push] Silent 재구독 완료');
      }
    } catch (err) {
      console.error('[Push] 구독 실패:', err);
      if (!silent) {
        setError(err.message || '푸시 알림 구독에 실패했습니다.');
      }
    } finally {
      setLoading(false);
    }
  };

  const unsubscribeFromPush = async () => {
    if (loading) return;
    
    try {
      setLoading(true);
      setError('');

      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        await subscription.unsubscribe();
        
        await api.post('/push/unsubscribe', {
          endpoint: subscription.endpoint
        });
      }

      setIsSubscribed(false);
      toast('푸시 알림이 비활성화되었습니다.');
    } catch (err) {
      console.error('[Push] 구독 취소 실패:', err);
      setError('푸시 알림 구독 취소에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const testPushNotification = async () => {
    try {
      await api.post('/push/test');
      toast('테스트 푸시를 발송했습니다. 잠시 후 알림을 확인하세요.');
    } catch (err) {
      console.error('[Push] 테스트 실패:', err);
      setError('테스트 푸시 발송에 실패했습니다.');
    }
  };

  const handleToggle = () => {
    if (loading) return;
    
    if (isSubscribed) {
      unsubscribeFromPush();
    } else {
      subscribeToPush(false);
    }
  };

  const handleResubscribe = async () => {
    await subscribeToPush(false);
  };

  const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');
    
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  };

  useEffect(() => {
    if (!('permissions' in navigator)) return;

    const handlePermissionChange = () => {
      const newPermission = Notification.permission;
      console.log('[Push] 알림 권한 변경:', newPermission);

      if (newPermission === 'granted' && !isSubscribed) {
        checkAndValidateSubscription();
      } else if (newPermission === 'denied') {
        setIsSubscribed(false);
      }
    };

    navigator.permissions.query({ name: 'notifications' }).then((permissionStatus) => {
      permissionStatus.onchange = handlePermissionChange;
    }).catch(err => {
      console.log('[Push] 권한 모니터링 불가:', err);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubscribed]);

  if (checking) {
    console.log('[Push Render] 초기 체크 중...');
    return null;
  }
  
  if (!isSupported) {
    console.log('[Push Render] 브라우저가 푸시를 지원하지 않음');
    return null;
  }

  const permissionGuide = getPermissionGuide();

  return (
    <>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {isSubscribed ? <Notifications color="primary" /> : <NotificationsOff color={permissionDenied ? 'error' : 'inherit'} />}
            <Box>
              <Typography variant="subtitle1" fontWeight={600}>
                푸시 알림
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 1 }}>
            {isSubscribed && (
              <Button
                size="small"
                variant="outlined"
                onClick={testPushNotification}
                disabled={loading}
              >
                테스트
              </Button>
            )}
            <FormControlLabel
              control={
                <Switch
                  checked={isSubscribed}
                  onChange={handleToggle}
                  disabled={loading || !vapidPublicKey || permissionDenied}
                />
              }
              label=""
            />
          </Box>
        </Box>

        {permissionDenied && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              알림 권한이 차단되어 있습니다
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
              이전에 알림을 거부하셨습니다. 앱에서는 직접 권한을 변경할 수 없습니다.
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', fontWeight: 500 }}>
              재활성화 방법: {permissionGuide.steps}
            </Typography>
            {permissionGuide.detail && (
              <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}>
                {permissionGuide.detail}
              </Typography>
            )}
          </Alert>
        )}

        {error && !permissionDenied && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        {isSubscribed && (
          <Alert severity="success" sx={{ mt: 2 }}>
            푸시 알림이 활성화되어 있습니다.
          </Alert>
        )}

        {!vapidPublicKey && (
          <Alert severity="info" sx={{ mt: 2 }}>
            서버에서 푸시 서비스 설정 중입니다. 잠시만 기다려주세요.
          </Alert>
        )}
      </Paper>

      <Snackbar
        open={showResubscribePrompt}
        autoHideDuration={null}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="warning"
          action={
            <>
              <Button 
                color="inherit" 
                size="small" 
                onClick={handleResubscribe}
                disabled={loading}
              >
                재등록
              </Button>
              <Button 
                color="inherit" 
                size="small" 
                onClick={() => setShowResubscribePrompt(false)}
              >
                닫기
              </Button>
            </>
          }
        >
          알림 구독이 만료되었습니다. 다시 등록하시겠습니까?
        </Alert>
      </Snackbar>
    </>
  );
}

export default PushNotificationManager;